import React, { useState, useEffect } from 'react';
import { Gift, Building2, ArrowRight, Calendar, CheckCircle, Clock, RefreshCw, DollarSign } from 'lucide-react';
import { supabase } from '../supabase';

export default function CustomerReferrals() {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, signed_up, subscribed

  useEffect(() => {
    loadReferrals();
  }, []);

  const loadReferrals = async () => {
    try {
      // Get companies that were referred (have referred_by_company_id set)
      const { data: referredCompanies, error: refError } = await supabase
        .from('companies')
        .select('id, name, subscription_status, created_at, referred_by_company_id, referral_code')
        .not('referred_by_company_id', 'is', null)
        .order('created_at', { ascending: false });

      if (refError) throw refError;

      // Get the referring company details
      const referrerIds = [...new Set((referredCompanies || []).map(c => c.referred_by_company_id))];
      let referrerMap = {};
      if (referrerIds.length > 0) {
        const { data: referrers } = await supabase
          .from('companies')
          .select('id, name')
          .in('id', referrerIds);
        (referrers || []).forEach(r => { referrerMap[r.id] = r; });
      }

      // Also check customer_referrals table for any additional data
      const { data: crData } = await supabase
        .from('customer_referrals')
        .select('*')
        .order('created_at', { ascending: false });

      // Build unified referral list from companies data
      const companyReferrals = (referredCompanies || []).map(c => ({
        id: c.id,
        referring_company: referrerMap[c.referred_by_company_id] || { name: 'Unknown' },
        referred_company: { id: c.id, name: c.name, subscription_status: c.subscription_status, created_at: c.created_at },
        referral_code: c.referral_code || '-',
        status: (c.subscription_status === 'active' || c.subscription_status === 'trialing') ? 'subscribed' : 'signed_up',
        signed_up_at: c.created_at,
        subscribed_at: c.subscription_status === 'active' ? c.created_at : null,
      }));

      // Merge in any customer_referrals records not already represented
      const existingIds = new Set(companyReferrals.map(r => r.referred_company?.id));
      const extraReferrals = (crData || []).filter(cr => !existingIds.has(cr.referred_company_id));

      setReferrals([...companyReferrals, ...extraReferrals]);
    } catch (error) {
      console.error('Error loading referrals:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredReferrals = referrals.filter(r => {
    if (filter === 'all') return true;
    return r.status === filter;
  });

  const stats = {
    total: referrals.length,
    signedUp: referrals.filter(r => r.status === 'signed_up').length,
    subscribed: referrals.filter(r => r.status === 'subscribed').length
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status, subscriptionStatus) => {
    if (status === 'subscribed' || subscriptionStatus === 'active') {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.35rem',
          padding: '0.35rem 0.75rem',
          background: '#1a3a2a',
          color: '#4ecca3',
          borderRadius: '20px',
          fontSize: '0.75rem',
          fontWeight: '600'
        }}>
          <CheckCircle size={12} />
          Subscribed
        </span>
      );
    }
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        padding: '0.35rem 0.75rem',
        background: '#2a2a1a',
        color: '#f7931e',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600'
      }}>
        <Clock size={12} />
        Signed Up
      </span>
    );
  };

  return (
    <div style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <Gift size={28} color="#4ecca3" />
            Customer Referrals
          </h1>
          <p style={{ color: '#888' }}>
            Track referrals from existing customers using their referral links
          </p>
        </div>
        <button
          onClick={loadReferrals}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.25rem',
            background: '#2a2a2a',
            border: '1px solid #3a3a3a',
            borderRadius: '8px',
            color: '#e0e0e0',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid #2a2a2a'
        }}>
          <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Total Referrals
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#e0e0e0' }}>
            {stats.total}
          </div>
        </div>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid #2a2a2a'
        }}>
          <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Signed Up
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#f7931e' }}>
            {stats.signedUp}
          </div>
        </div>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid #2a2a2a'
        }}>
          <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Subscribed
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#4ecca3' }}>
            {stats.subscribed}
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem'
      }}>
        {[
          { id: 'all', label: 'All' },
          { id: 'signed_up', label: 'Signed Up' },
          { id: 'subscribed', label: 'Subscribed' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            style={{
              padding: '0.5rem 1rem',
              background: filter === tab.id ? '#ff6b35' : '#2a2a2a',
              border: 'none',
              borderRadius: '6px',
              color: filter === tab.id ? '#fff' : '#888',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: filter === tab.id ? '600' : '400'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Referrals Table */}
      {loading ? (
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          padding: '3rem',
          textAlign: 'center',
          color: '#888',
          border: '1px solid #2a2a2a'
        }}>
          Loading referrals...
        </div>
      ) : filteredReferrals.length === 0 ? (
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          padding: '3rem',
          textAlign: 'center',
          border: '1px solid #2a2a2a'
        }}>
          <Gift size={48} color="#3a3a3a" style={{ marginBottom: '1rem' }} />
          <p style={{ color: '#888' }}>
            {filter === 'all'
              ? 'No customer referrals yet'
              : `No referrals with "${filter.replace('_', ' ')}" status`
            }
          </p>
        </div>
      ) : (
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          border: '1px solid #2a2a2a',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                <th style={{
                  textAlign: 'left',
                  padding: '1rem 1.25rem',
                  color: '#888',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Referring Customer
                </th>
                <th style={{
                  textAlign: 'center',
                  padding: '1rem 1.25rem',
                  color: '#888',
                  fontWeight: '600',
                  fontSize: '0.8rem'
                }}>

                </th>
                <th style={{
                  textAlign: 'left',
                  padding: '1rem 1.25rem',
                  color: '#888',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  New Customer
                </th>
                <th style={{
                  textAlign: 'left',
                  padding: '1rem 1.25rem',
                  color: '#888',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Code
                </th>
                <th style={{
                  textAlign: 'left',
                  padding: '1rem 1.25rem',
                  color: '#888',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Status
                </th>
                <th style={{
                  textAlign: 'left',
                  padding: '1rem 1.25rem',
                  color: '#888',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Signed Up
                </th>
                <th style={{
                  textAlign: 'left',
                  padding: '1rem 1.25rem',
                  color: '#888',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Subscribed
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredReferrals.map(referral => (
                <tr
                  key={referral.id}
                  style={{
                    borderBottom: '1px solid #2a2a2a'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#1f1f1f'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '1rem 1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        background: '#2a2a2a',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Building2 size={18} color="#4ecca3" />
                      </div>
                      <div>
                        <div style={{ color: '#e0e0e0', fontWeight: '500' }}>
                          {referral.referring_company?.name || 'Unknown'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem 0.5rem', textAlign: 'center' }}>
                    <ArrowRight size={16} color="#3a3a3a" />
                  </td>
                  <td style={{ padding: '1rem 1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        background: '#2a2a2a',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Building2 size={18} color="#ff6b35" />
                      </div>
                      <div>
                        <div style={{ color: '#e0e0e0', fontWeight: '500' }}>
                          {referral.referred_company?.name || 'Unknown'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem 1.25rem' }}>
                    <code style={{
                      background: '#2a2a2a',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      color: '#888',
                      fontSize: '0.85rem'
                    }}>
                      {referral.referral_code}
                    </code>
                  </td>
                  <td style={{ padding: '1rem 1.25rem' }}>
                    {getStatusBadge(referral.status, referral.referred_company?.subscription_status)}
                  </td>
                  <td style={{ padding: '1rem 1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#888', fontSize: '0.9rem' }}>
                      <Calendar size={14} />
                      {formatDate(referral.signed_up_at)}
                    </div>
                  </td>
                  <td style={{ padding: '1rem 1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#888', fontSize: '0.9rem' }}>
                      {referral.subscribed_at ? (
                        <>
                          <DollarSign size={14} color="#4ecca3" />
                          <span style={{ color: '#4ecca3' }}>{formatDate(referral.subscribed_at)}</span>
                        </>
                      ) : (
                        '-'
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
